import { CATEGORIES, type CategoryKey } from "../categories/generated.js";
import { normalizeName } from "../parser/item-input.js";
import { matchProduct } from "../catalog/match.js";
import type { List, Product, Unit } from "../types/entities.js";
import { UNSICHER_UNTER, type AiAction, type AiItem, type AiResult } from "./schema.js";

/** Ein Artikel, wie ihn die Vorschau-Karte zeigt und die App anlegen würde. */
export interface ResolvedItem {
  name: string;
  quantity: number | null;
  unit: Unit | null;
  category: CategoryKey | null;
  productId: string | null;
  confidence: number;
  needsClarification: boolean;
  alternatives: string[];
}

export interface ResolvedAction {
  type: AiAction["type"];
  listId: string | null;
  listName: string;
  listConfidence: number;
  items: ResolvedItem[];
}

export interface ResolvedResult {
  actions: ResolvedAction[];
  summary: string;
  /** Alles, was jemand vor dem Übernehmen ansehen sollte. */
  needsAttention: boolean;
}

const KATEGORIE_SCHLUESSEL = new Set<string>(CATEGORIES.map((eintrag) => eintrag.key));
const EINHEITEN = new Set(["stk", "g", "kg", "ml", "l", "pkg", "dose", "glas", "flasche", "bund", "becher"]);

export interface ResolveContext {
  lists: readonly List[];
  products: readonly Product[];
  /** Liste, die gerade offen ist. Sie gewinnt, wenn das Modell unsicher war. */
  currentListId?: string | null;
}

/**
 * Bereitet die Antwort des Modells so auf, dass die App damit arbeiten kann.
 *
 * Hier passiert die Arbeit, die man einem Sprachmodell nicht überlassen
 * sollte: Listen zuordnen, Kategorien gegen die kanonische Liste prüfen,
 * Produkte aus dem Familienkatalog nachschlagen, Unsinn wegwerfen. Das Modell
 * schlägt vor, der Kern entscheidet.
 */
export function resolveAiResult(result: AiResult, context: ResolveContext): ResolvedResult {
  const actions = result.actions
    .map((action) => resolveAction(action, context))
    .filter((action) => action.type === "create_list" || action.items.length > 0);

  const needsAttention = actions.some(
    (action) =>
      action.listConfidence < UNSICHER_UNTER ||
      action.items.some((item) => item.needsClarification || item.confidence < UNSICHER_UNTER),
  );

  return { actions, summary: result.summary, needsAttention };
}

/** Sicherheit, mit der eine geratene Liste höchstens angeboten wird. */
const GERATEN_HOECHSTENS = 0.5;

function resolveAction(action: AiAction, context: ResolveContext): ResolvedAction {
  const { list, matched } = findList(action, context);

  // Eine Liste, auf die nur ausgewichen wurde, ist nie sicher, egal wie
  // selbstbewusst das Modell klingt. Sonst übernimmt jemand ungeprüft, und
  // die Sonnencreme liegt im Baumarkt.
  const confidence = list === null ? 0 : matched ? action.list_confidence : Math.min(action.list_confidence, GERATEN_HOECHSTENS);

  return {
    type: action.type,
    listId: list?.id ?? null,
    listName: list?.name ?? action.list_name ?? "",
    listConfidence: confidence,
    items: action.items.map((item) => resolveItem(item, context)),
  };
}

/**
 * Findet die gemeinte Liste: erst über die ID, dann über den Namen, sonst über
 * die gerade geöffnete Liste, sonst über die Standardliste. Lieber die falsche
 * Liste vorschlagen und sichtbar unsicher sein als gar nichts anzubieten.
 */
function findList(action: AiAction, context: ResolveContext): { list: List | null; matched: boolean } {
  const lebendig = context.lists.filter((list) => list.deleted_at === null);

  const perId = action.list_id !== null ? lebendig.find((list) => list.id === action.list_id) : undefined;
  if (perId) return { list: perId, matched: true };

  if (action.list_name !== null) {
    const gesucht = normalizeName(action.list_name);
    const perName = lebendig.find((list) => normalizeName(list.name) === gesucht);
    if (perName) return { list: perName, matched: true };

    const teilweise = lebendig.find((list) => normalizeName(list.name).includes(gesucht) && gesucht.length >= 3);
    if (teilweise) return { list: teilweise, matched: true };
  }

  if (action.type === "create_list") return { list: null, matched: true };

  const offen = context.currentListId ? lebendig.find((list) => list.id === context.currentListId) : undefined;
  if (offen) return { list: offen, matched: action.list_name === null };

  const ersatz = lebendig.find((list) => list.is_default) ?? lebendig[0] ?? null;
  return { list: ersatz, matched: action.list_name === null && action.list_id === null };
}

function resolveItem(item: AiItem, context: ResolveContext): ResolvedItem {
  const name = item.name.trim();
  const katalog = matchProduct(name, context.products);

  const produkt = item.product_id !== null
    ? (context.products.find((p) => p.id === item.product_id && p.deleted_at === null) ?? katalog.product)
    : katalog.product;

  // Das Modell darf Kategorien vorschlagen, aber nur aus der kanonischen Liste.
  const kategorie =
    item.category !== null && KATEGORIE_SCHLUESSEL.has(item.category)
      ? (item.category as CategoryKey)
      : (produkt?.category ?? katalog.category);

  const einheit = item.unit !== null && EINHEITEN.has(item.unit) ? (item.unit as Unit) : (produkt?.default_unit ?? null);

  return {
    name,
    quantity: item.qty,
    unit: einheit,
    category: kategorie,
    productId: produkt?.id ?? null,
    confidence: item.confidence,
    // Ein Sammelbegriff bleibt einer, auch wenn das Modell sicher klingt.
    needsClarification: item.needs_clarification || item.confidence < UNSICHER_UNTER,
    alternatives: item.alternatives.slice(0, 2),
  };
}

/** Wie viele Artikel insgesamt angelegt würden. Für die Bestätigung danach. */
export function countItems(result: ResolvedResult): number {
  return result.actions.reduce((sum, action) => sum + action.items.length, 0);
}
